import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { Controller, useFormContext } from "react-hook-form";
import { uniqBy } from "lodash";
import { fetchApi } from "../../../../../../app/javascript/helpers/reactQueryApi";
import type { ComposerExtraFieldsProps } from "../../../../../../app/javascript/components/activitystreams/composer/ComposerExtraFields";
import SpvCatalogSelect from "./SpvCatalogSelect";
import type { Catalog, Catalogs, TagOption } from "./types";

// Zielgruppen and Themen (tixxt/core#364), required for new posts and for edits of posts that carry a selection
function catalogTagIds(catalog: Catalog): Set<string> {
  return new Set([
    ...catalog.groups.flatMap((group) =>
      group.entries.map((entry) => entry.tag_id),
    ),
    ...(catalog.retired_tag_ids ?? []),
  ]);
}

function allCatalogTagIds(catalogs: Catalogs): Set<string> {
  return new Set(
    Object.values(catalogs).flatMap((catalog) => [...catalogTagIds(catalog)]),
  );
}

function missingCatalogs(catalogs: Catalogs, selected: TagOption[]): string[] {
  return Object.entries(catalogs)
    .filter(([, catalog]) => {
      const ids = catalogTagIds(catalog);
      return !selected.some((tag) => ids.has(tag.value));
    })
    .map(([key]) => key);
}

export default function SpvCatalogFields({
  activity,
  activityType,
}: ComposerExtraFieldsProps) {
  const enabled = activityType !== "appointment";
  const { data: catalogs } = useQuery<Catalogs>(
    "spv-post-catalogs",
    () => fetchApi("/custom/spv/post_catalogs"),
    { staleTime: Infinity, enabled },
  );
  const { watch, setValue, control, formState } = useFormContext();
  const tags = watch("tags") as TagOption[] | undefined;
  const extraTags = watch("extraTags") as TagOption[] | undefined;

  // on edit the post's catalog tags arrive in `tags`; move them into the catalog fields
  useEffect(() => {
    if (!enabled || !catalogs) return;

    const ids = allCatalogTagIds(catalogs);
    const claimed = (tags ?? []).filter((tag) => ids.has(tag.value));
    if (claimed.length === 0) return;

    setValue(
      "tags",
      (tags ?? []).filter((tag) => !ids.has(tag.value)),
    );
    setValue("extraTags", uniqBy([...(extraTags ?? []), ...claimed], "value"));
  }, [enabled, catalogs, tags, extraTags, setValue]);

  // posts from before the feature stay saveable without a selection; decided once, before the claim above
  const grandfathered = useRef<boolean | null>(null);
  if (catalogs && grandfathered.current === null) {
    const ids = allCatalogTagIds(catalogs);
    const hasCatalogTags = [...(tags ?? []), ...(extraTags ?? [])].some((tag) =>
      ids.has(tag.value),
    );
    grandfathered.current = Boolean(activity) && !hasCatalogTags;
  }

  if (!enabled || !catalogs) return null;

  const required = grandfathered.current === false;
  const missing = required ? missingCatalogs(catalogs, extraTags ?? []) : [];

  return (
    <Controller
      name="extraTags"
      control={control}
      rules={{
        validate: (value: TagOption[] | undefined) => {
          const titles = required
            ? missingCatalogs(catalogs, value ?? []).map(
                (key) => catalogs[key].title,
              )
            : [];
          return (
            titles.length === 0 ||
            `Bitte wählen Sie ${titles.join(" und ")} aus.`
          );
        },
      }}
      render={({ field }) => (
        <div className="spv-catalog-fields flex flex-col gap-4">
          {Object.entries(catalogs).map(([key, catalog]) => {
            const ids = catalogTagIds(catalog);

            return (
              <SpvCatalogSelect
                key={key}
                catalog={catalog}
                selection={(field.value ?? []).filter((tag: TagOption) =>
                  ids.has(tag.value),
                )}
                onChange={(selected) =>
                  field.onChange([
                    ...(field.value ?? []).filter(
                      (tag: TagOption) => !ids.has(tag.value),
                    ),
                    ...selected,
                  ])
                }
                required={required}
                invalid={formState.isSubmitted && missing.includes(key)}
              />
            );
          })}
        </div>
      )}
    />
  );
}
